/**
 * Outages API
 * Filtered list + compressed search for outage overview
 * Detail fetchers for outage detail page
 */

import {supabase} from './client'
import {getRowsFiltered, ListParams, ListResponse} from "@/api/index"
import {AssetFetchParams, AssetFetchResult} from "@/types/Asset"
import type {Outage} from "@/types/Outage"

const OUTAGE_STATS_SELECT = "id, code, name, description, scheduled_start, scheduled_end, planned_hours, order_count, order_state_counts, task_count, task_state_counts, checklist_count, checklist_state_counts"


/**
 * Get paginated, sorted, filtered outages with stats
 * Uses the outages_with_stats view via filter_rows RPC
 */
export async function getOutagesFiltered(params: ListParams): Promise<ListResponse<Outage>> {
    return getRowsFiltered(params, "outages_with_stats", OUTAGE_STATS_SELECT)
}


/**
 * Get a single outage by code (with all aggregated stats for pie charts)
 */
export async function getOutageByCode(code: string): Promise<Outage | null> {
    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: "outages_with_stats",
        p_filters: {code},
        p_sort_column: 'id',
        p_sort_order: 'asc',
        p_limit: 1,
        p_offset: 0,
        p_select: OUTAGE_STATS_SELECT
    })

    if (error) throw error
    return data?.items?.[0] ?? null
}


/**
 * Get paginated orders for an outage
 */
export async function getOutageOrders(code: string, params: ListParams): Promise<ListResponse<any>> {
    return getRowsFiltered(
        {...params, filters: {...params.filters, outage_code: code}},
        "orders_with_progress",
        "id, title, work_group, state, priority, progress, scheduled_start, scheduled_end, outage_code, task_count, completed_task_count"
    )
}


/**
 * Get paginated tasks for an outage
 */
export async function getOutageTasks(code: string, params: ListParams): Promise<ListResponse<any>> {
    return getRowsFiltered(
        {...params, filters: {...params.filters, outage_code: code}},
        "tasks_with_details",
        "uuid, id, order_id, title, assignee, assignee_name, state, priority, is_lockout, scheduled_duration, work_group, outage_code"
    )
}


/**
 * Get paginated checklists for an outage
 */
export async function getOutageChecklists(code: string, params: ListParams): Promise<ListResponse<any>> {
    return getRowsFiltered(
        {...params, filters: {...params.filters, outage_code: code}},
        "checklists_with_details",
        "id, title, assignees, assignee_names, state, work_group, item_count, checked_count, nio_count, outage_code"
    )
}


/**
 * Compressed outage search for AssetSearch autocomplete
 * Uses base outages table (no stats needed for search dropdown)
 */
export async function getCompressedOutages(params: AssetFetchParams, baseFilter: Record<string, any> = {}): Promise<AssetFetchResult> {
    const {
        offset = 0,
        limit = 100,
        search = ""
    } = params

    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: "outages",
        p_filters: baseFilter,
        p_sort_column: 'code',
        p_sort_order: 'asc',
        p_limit: limit,
        p_offset: offset,
        p_select: "id, code, name",
        p_search: search,
        p_search_columns: ['code', 'name']
    })

    if (error) throw error

    return {
        items: (data?.items || []).map((d: any) => ({
            title: d.code,
            subtitle: d.name || '',
            prependIcon: 'mdi-power',
            code: d.code,
            id: d.id
        })),
        total: data?.total || 0,
    }
}
